import React, { useEffect, useState } from 'react';
import { Form, FilterGrid, FilterField, type SearchFieldProps } from '@/components/ui';
import FiltersCard from '../../../components/FiltersCard';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { areasService } from '../../../services/areasService';

export interface VendorFilterState {
  statuses: string[];
  serviceAreaIds: number[];
  complianceIssuesOnly: boolean;
}

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending Approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

/**
 * Vendor status is a real workflow, so it keeps its Status dropdown (V2-075, INACT-02), but it
 * opens on Active + Pending Approval: inactive vendors are hidden by default (INACT-04).
 * Clear All returns here.
 */
export const DEFAULT_VENDOR_STATUSES = ['PENDING', 'ACTIVE'];

export const DEFAULT_VENDOR_FILTERS: VendorFilterState = {
  statuses: DEFAULT_VENDOR_STATUSES,
  serviceAreaIds: [],
  complianceIssuesOnly: false,
};

/** Kept for callers that mean "no filters at all". */
export const EMPTY_VENDOR_FILTERS: VendorFilterState = {
  statuses: [],
  serviceAreaIds: [],
  complianceIssuesOnly: false,
};

const sameList = (a: Array<string | number>, b: Array<string | number>) =>
  a.length === b.length && [...a].map(String).sort().join('|') === [...b].map(String).sort().join('|');

/** True when the Status filter hides some vendors (none or every status selected hides nothing). */
export const statusFilterHides = (statuses: string[]): boolean =>
  statuses.length > 0 && STATUS_OPTIONS.some((o) => !statuses.includes(o.value));

/**
 * Every filter that hides vendors, the default Status (Active + Pending Approval) included
 * (V2-076, FLT-05) - the collapsed bar's "n filters applied".
 */
export const countVendorFilters = (f: VendorFilterState): number =>
  [statusFilterHides(f.statuses), f.serviceAreaIds.length > 0, f.complianceIssuesOnly].filter(Boolean).length;

/** True when two filter states differ (pending vs applied enables Apply; applied vs default enables Clear All). */
export const vendorFiltersDiffer = (a: VendorFilterState, b: VendorFilterState): boolean =>
  !sameList(a.statuses, b.statuses) ||
  !sameList(a.serviceAreaIds, b.serviceAreaIds) ||
  a.complianceIssuesOnly !== b.complianceIssuesOnly;

interface Props {
  /** The PENDING filters - the controls edit these; nothing is fetched until Apply. */
  filters: VendorFilterState;
  onFilterChange: (filters: VendorFilterState) => void;
  onClearFilters: () => void;
  onApply: () => void;
  /** Pass `!hasUnappliedChanges`. */
  applyDisabled: boolean;
  /** True when the APPLIED filters (or the search) differ from the defaults. */
  hasActiveFilters: boolean;
  /** Every applied filter hiding vendors, defaults included (`countVendorFilters`). */
  appliedFilterCount: number;
  /** The page's search, shown in the panel's header bar (V2-077). */
  search: SearchFieldProps;
}

const VendorsFilters: React.FC<Props> = ({
  filters,
  onFilterChange,
  onClearFilters,
  onApply,
  applyDisabled,
  hasActiveFilters,
  appliedFilterCount,
  search,
}) => {
  const [serviceAreas, setServiceAreas] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    areasService
      .getServiceAreas()
      .then((areas: any[]) => setServiceAreas(areas ?? []))
      .catch(() => setServiceAreas([]));
  }, []);

  const set = <K extends keyof VendorFilterState>(field: K, value: VendorFilterState[K]) =>
    onFilterChange({ ...filters, [field]: value });

  return (
    <FiltersCard
      title="Vendor Filters"
      subtitle="Filter by status, service area, and compliance"
      collapsible
      defaultCollapsed={false}
      onApply={onApply}
      applyDisabled={applyDisabled}
      onClear={onClearFilters}
      hasActiveFilters={hasActiveFilters}
      appliedFilterCount={appliedFilterCount}
      search={search}
    >
      <FilterGrid>
        <FilterField>
          <MultiSelectDropdown
            options={STATUS_OPTIONS}
            value={filters.statuses}
            onChange={(v) => set('statuses', v.map(String))}
            getValue={(o) => o.value}
            formatOption={(o) => o.label}
            placeholder="All Statuses"
            label="Status"
            className="tw-w-full"
            showSearch={false}
            // Lifecycle order (pending -> active -> inactive) reads better than alphabetical.
            preserveOptionsOrder
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            options={serviceAreas}
            value={filters.serviceAreaIds}
            onChange={(v) => set('serviceAreaIds', v.map(Number))}
            getValue={(o) => o.id}
            formatOption={(o) => o.name}
            placeholder="All Service Areas"
            label="Service Area"
            className="tw-w-full"
          />
        </FilterField>
        <FilterField span={2}>
          <Form.Group>
            {/*
              HE-M18 is an indicator, not a gate - but a coordinator chasing lapsed
              certificates needs to find them, and scanning every row for a triangle does not
              scale. This narrows to exactly those, without blocking anything.
            */}
            <Form.Label>Compliance</Form.Label>
            <Form.Check
              type="checkbox"
              id="compliance-issues-only"
              label="Only vendors with expired or missing WCB / liability insurance"
              checked={filters.complianceIssuesOnly}
              onChange={(e) => set('complianceIssuesOnly', e.target.checked)}
            />
          </Form.Group>
        </FilterField>
      </FilterGrid>
    </FiltersCard>
  );
};

export default VendorsFilters;
